import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail } from 'lucide-react';
import { FaFacebookF, FaInstagram, FaWhatsapp, FaLinkedinIn } from 'react-icons/fa';
import logo from "../assets/Images/logo.jpg";

const Footer = () => {
  const services = [
    {
      name: 'Hydraulic Cylinder Refurbishment',
      link: 'hydraulic-cylinder-refurbishment',
      hasIndividualPage: true
    },
    {
      name: 'Hydraulic Pump Reconditioning',
      link: 'hydraulic-pump-reconditioning',
      hasIndividualPage: true
    },
    {
      name: 'Hydraulic Motors Refurbish',
      link: 'hydraulic-motors-refurbish',
      hasIndividualPage: true
    },
    {
      name: 'Onsite Hydraulic Services',
      link: 'onsite-hydraulic-services',
      hasIndividualPage: true
    },
    {
      name: 'Rock Breaker Refurbishment',
      link: 'rock-breaker-refurbishment',
      hasIndividualPage: true
    },
    {
      name: 'Hydraulic Circuit Blocks',
      link: 'hydraulic-circuit-blocks',
      hasIndividualPage: true
    },
    {
      name: 'Control Valve Refurbishment',
      link: 'control-valve-refurbishment',
      hasIndividualPage: true
    },
    {
      name: 'Undercarriage Refurbishment',
      link: 'undercarriage-refurbishment',
      hasIndividualPage: true
    },
    {
      name: 'Hydraulic pumps and Diagnostics',
      link: 'hydraulic-pumps-and-diagnostics',
      hasIndividualPage: false
    }
  ];

  return (
    <footer className="footer bg-gray-900 text-white py-12">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info with Social Media */}
          <div className="footer-section">
            <div className="flex flex-col items-start mb-4">
              <img
                src={logo}
                alt="Hospet Hydraulics Solutions Logo"
                className="h-12 sm:h-16 w-auto object-contain mb-2"
              />
              <h3 className="text-lg sm:text-xl font-bold text-blue-400">Hospet Hydraulics Solutions</h3>
              <h5 className="text-base sm:text-lg font-bold">Fluid Expertise, Trusted Solutions</h5>
            </div>
            <p className="text-gray-300 mb-6 text-sm sm:text-base">
              Premier service provider specializing in the maintenance and repair
              of heavy earth-moving equipment. We ensure your equipment operates
              at peak efficiency.
            </p>

            {/* Social Media */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-blue-400 mb-3">FOLLOW US</h4>
              <div className="flex space-x-3">
                <a
                  href="https://www.facebook.com/yourpage"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-gray-800 hover:bg-blue-600 flex items-center justify-center text-white transition-colors duration-200"
                  aria-label="Facebook"
                >
                  <FaFacebookF className="w-3.5 h-3.5" />
                </a>
                <a
                  href="https://www.instagram.com/yourprofile"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-gray-800 hover:bg-pink-600 flex items-center justify-center text-white transition-colors duration-200"
                  aria-label="Instagram"
                >
                  <FaInstagram className="w-3.5 h-3.5" />
                </a>
                <a
                  href="https://wa.me/919876543210"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-gray-800 hover:bg-green-500 flex items-center justify-center text-white transition-colors duration-200"
                  aria-label="WhatsApp"
                >
                  <FaWhatsapp className="w-3.5 h-3.5" />
                </a>
                <a
                  href="https://www.linkedin.com/company/yourcompany"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-gray-800 hover:bg-blue-700 flex items-center justify-center text-white transition-colors duration-200"
                  aria-label="LinkedIn"
                >
                  <FaLinkedinIn className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="footer-section">
            <h3 className="text-lg sm:text-xl font-bold mb-4 text-blue-400">Quick Links</h3>
            <div className="flex flex-col space-y-2 text-sm sm:text-base">
              <Link
                to="/"
                className="text-gray-300 hover:text-white transition-colors"
              >Home</Link>
              <Link
                to="/about"
                className="text-gray-300 hover:text-white transition-colors"
              >About Us</Link>
              <Link
                to="/portfolio"
                className="text-gray-300 hover:text-white transition-colors"
              >Our Portfolio</Link>
              <Link
                to="/services"
                className="text-gray-300 hover:text-white transition-colors"
              >Services</Link>
              <Link
                to="/contact"
                className="text-gray-300 hover:text-white transition-colors"
              >Contact</Link>
            </div>
          </div>

          {/* Our Services */}
          <div className="footer-section">
            <h3 className="text-lg sm:text-xl font-bold mb-4 text-blue-400">Our Services</h3>
            <div className="grid grid-cols-1 gap-2 text-sm sm:text-base">
              {services.map((service, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Link
                    to={service.hasIndividualPage ? `/services/${service.link}` : '/services#other-services'}
                    className="text-gray-300 hover:text-white transition-colors"
                    onClick={() => {
                      if (!service.hasIndividualPage && window.location.pathname.startsWith('/services')) {
                        const section = document.getElementById('other-services');
                        if (section) {
                          section.scrollIntoView({ behavior: 'smooth' });
                        }
                      }
                    }}
                  >
                    {service.name}
                  </Link>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div className="footer-section">
            <h3 className="text-lg sm:text-xl font-bold mb-4 text-blue-400">Contact Info</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin className="text-blue-400 mt-1 flex-shrink-0" size={20} />
                <a
                  href="https://maps.google.com/?cid=1451351141381342969"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="not-italic text-gray-300 hover:text-white transition-colors text-sm sm:text-base block"
                >
                  SVN Nilaya, D.No. 397/A,<br />
                  31st Ward, 1st Cross, Nehru Colony,<br />
                  Hosapete - 583 201,<br />
                  Vijayanagara Dist., Karnataka
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-full bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                  <Phone className="text-blue-400" size={16} />
                </div>
                <div className="flex flex-col text-sm sm:text-base">

                  <a href="tel:+919448145300" className="text-gray-300 hover:text-white transition-colors">+91 94481 45300</a>
                  <a href="tel:+919980944559" className="text-gray-300 hover:text-white transition-colors">+91 99809 44559</a>
                  <a href="tel:+919448334437" className="text-gray-300 hover:text-white transition-colors">+91 94483 34437</a>
                </div>
              </div>
              <div className="flex items-center space-x-3 overflow-hidden">
                <div className="w-8 h-8 rounded-full bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                  <Mail className="text-blue-400" size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <a
                    href="mailto:hospethydraulicsolutions@gmail.com"
                    className="text-gray-300 hover:text-white transition-colors text-[13px] sm:text-sm lg:text-[11px] xl:text-[13px] truncate block"
                    title="hospethydraulicsolutions@gmail.com"
                  >
                    hospethydraulicsolutions@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400 text-xs sm:text-sm">
          <p>&copy; {new Date().getFullYear()} Hospet Hydraulics Solutions. All rights reserved.</p>
          <p className="mt-2 text-[10px] sm:text-xs text-gray-500">
            <em>Note: Machinery images shown on this website are for reference purposes only.</em>
          </p>
          {/* <p className="mt-2 text-sm">
            Developed by{' '}
            <a 
              href="https://www.nuyanthratechsolutions.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 transition-colors"
            >
              NuYanthra Tech Solutions
            </a>
          </p> */}
        </div>
      </div>
    </footer>
  );
};

export default Footer;